import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../../context/AnalyticsContext';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { 
  MessageCircle, 
  Lightbulb, 
  TrendingUp, 
  BarChart3, 
  PieChart, 
  Calendar,
  Users,
  DollarSign,
  Target,
  Zap,
  ChevronRight,
  Sparkles
} from 'lucide-react';

interface AnalyticsChatSuggestionsProps {
  provider: 'zoho' | 'powerbi';
  onSuggestionClick: (suggestion: string) => void;
}

interface SuggestionCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  suggestions: ChatSuggestion[];
}

interface ChatSuggestion {
  id: string;
  text: string;
  description: string;
  category: string;
  analyticsContext: {
    queryType: 'aggregation' | 'filter' | 'visualization' | 'insight';
    suggestedVisualization?: 'chart' | 'table' | 'metric';
  };
}

const AnalyticsChatSuggestions: React.FC<AnalyticsChatSuggestionsProps> = ({ 
  provider, 
  onSuggestionClick 
}) => {
  const { state } = useAnalytics();
  const [selectedCategory, setSelectedCategory] = useState<string>('getting-started');
  const [availableTables, setAvailableTables] = useState<string[]>([]);

  const schema = state.schema[provider];

  useEffect(() => {
    if (schema?.data?.tables) {
      setAvailableTables(schema.data.tables.map(table => table.name));
    }
  }, [schema]);

  const categories: SuggestionCategory[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <Sparkles className="w-5 h-5" />,
      color: 'blue',
      suggestions: [
        {
          id: 'explore-data',
          text: 'What data is available in my workspace?',
          description: 'Get an overview of all tables and data sources',
          category: 'getting-started',
          analyticsContext: {
            queryType: 'insight',
            suggestedVisualization: 'table'
          }
        },
        {
          id: 'data-overview',
          text: 'Show me a summary of my data',
          description: 'Get key metrics and data counts',
          category: 'getting-started',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'metric'
          }
        },
        {
          id: 'recent-trends',
          text: 'What are the recent trends in my data?',
          description: 'Identify patterns and changes over time',
          category: 'getting-started',
          analyticsContext: {
            queryType: 'insight',
            suggestedVisualization: 'chart'
          }
        }
      ]
    },
    {
      id: 'sales-analysis',
      title: 'Sales Analysis',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'green',
      suggestions: [
        {
          id: 'sales-performance',
          text: 'How are sales performing this month?',
          description: 'Monthly sales performance metrics',
          category: 'sales-analysis',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'chart'
          }
        },
        {
          id: 'top-products',
          text: 'What are our top-selling products?',
          description: 'Identify best-performing products',
          category: 'sales-analysis',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'table'
          }
        },
        {
          id: 'sales-by-region',
          text: 'Show sales performance by region',
          description: 'Geographic sales distribution',
          category: 'sales-analysis',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'chart'
          }
        },
        {
          id: 'revenue-forecast',
          text: 'Predict revenue for next quarter',
          description: 'Revenue forecasting based on trends',
          category: 'sales-analysis',
          analyticsContext: {
            queryType: 'insight',
            suggestedVisualization: 'chart'
          }
        }
      ]
    },
    {
      id: 'customer-insights',
      title: 'Customer Insights',
      icon: <Users className="w-5 h-5" />,
      color: 'purple',
      suggestions: [
        {
          id: 'customer-segments',
          text: 'Analyze customer segments',
          description: 'Break down customers by behavior and value',
          category: 'customer-insights',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'chart'
          }
        },
        {
          id: 'customer-retention',
          text: 'What is our customer retention rate?',
          description: 'Track customer loyalty and churn',
          category: 'customer-insights',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'metric'
          }
        },
        {
          id: 'high-value-customers',
          text: 'Who are our highest value customers?',
          description: 'Identify most profitable customers',
          category: 'customer-insights',
          analyticsContext: {
            queryType: 'filter',
            suggestedVisualization: 'table'
          }
        }
      ]
    },
    {
      id: 'operational',
      title: 'Operations',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'orange',
      suggestions: [
        {
          id: 'performance-kpis',
          text: 'Show me key performance indicators',
          description: 'Dashboard of essential business metrics',
          category: 'operational',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'metric'
          }
        },
        {
          id: 'efficiency-metrics',
          text: 'How efficient are our operations?',
          description: 'Operational efficiency analysis',
          category: 'operational',
          analyticsContext: {
            queryType: 'insight',
            suggestedVisualization: 'chart'
          }
        },
        {
          id: 'cost-analysis',
          text: 'Analyze costs and expenses',
          description: 'Break down operational costs',
          category: 'operational',
          analyticsContext: {
            queryType: 'aggregation',
            suggestedVisualization: 'chart'
          }
        }
      ]
    }
  ];

  const getTableSpecificSuggestions = (): ChatSuggestion[] => {
    if (availableTables.length === 0) return [];

    return availableTables.slice(0, 3).map(tableName => ({
      id: `table-${tableName}`,
      text: `Analyze data from ${tableName}`,
      description: `Get insights from the ${tableName} table`,
      category: 'table-specific',
      analyticsContext: {
        queryType: 'insight',
        suggestedVisualization: 'table'
      }
    }));
  };

  const getColorClasses = (color: string, variant: 'bg' | 'text' | 'border' = 'bg') => {
    const colorMap = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
      green: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
    };
    return colorMap[color as keyof typeof colorMap]?.[variant] || colorMap.blue[variant];
  };

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
  const tableSpecificSuggestions = getTableSpecificSuggestions();

  const handleSuggestionClick = (suggestion: ChatSuggestion) => {
    onSuggestionClick(suggestion.text);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Analytics Chat Suggestions</h3>
          <p className="text-text-secondary text-sm">
            Start with these common analytics questions or create your own.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Lightbulb className="w-5 h-5 text-yellow-600" />
          <span className="text-sm text-yellow-600 font-medium">Smart suggestions</span>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              selectedCategory === category.id
                ? `${getColorClasses(category.color, 'bg')} ${getColorClasses(category.color, 'text')}`
                : 'text-gray-600 hover:text-gray-900 hover:bg-white'
            }`}
          >
            {category.icon}
            <span>{category.title}</span>
          </button>
        ))}
      </div>

      {/* Suggestions Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {selectedCategoryData?.suggestions.map((suggestion) => (
          <Card
            key={suggestion.id}
            className="p-4 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => handleSuggestionClick(suggestion)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-text-primary mb-2">{suggestion.text}</h4>
                <p className="text-sm text-text-secondary mb-3">{suggestion.description}</p>
                <div className="flex items-center space-x-2">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    getColorClasses(selectedCategoryData.color, 'bg')
                  } ${getColorClasses(selectedCategoryData.color, 'text')}`}>
                    {suggestion.analyticsContext.queryType}
                  </div>
                  {suggestion.analyticsContext.suggestedVisualization && (
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      {suggestion.analyticsContext.suggestedVisualization === 'chart' && <BarChart3 className="w-3 h-3" />}
                      {suggestion.analyticsContext.suggestedVisualization === 'table' && <Target className="w-3 h-3" />}
                      {suggestion.analyticsContext.suggestedVisualization === 'metric' && <TrendingUp className="w-3 h-3" />}
                      <span>{suggestion.analyticsContext.suggestedVisualization}</span>
                    </div>
                  )}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-2" />
            </div>
          </Card>
        ))}
      </div>

      {/* Table-Specific Suggestions */}
      {tableSpecificSuggestions.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-text-primary mb-3">
            Your Data Tables
          </h4>
          <div className="grid gap-3 md:grid-cols-3">
            {tableSpecificSuggestions.map((suggestion) => (
              <Card
                key={suggestion.id}
                className="p-3 hover:shadow-md transition-shadow cursor-pointer border-dashed"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-text-primary text-sm">{suggestion.text}</h5>
                    <p className="text-xs text-text-secondary mt-1">{suggestion.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <MessageCircle className="w-8 h-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-800 mb-2">Need Help Getting Started?</h3>
            <p className="text-blue-700 text-sm mb-4">
              You can ask questions in natural language about your data. Try starting with simple questions 
              like "Show me sales data" or "What are my top customers" and I'll help you explore your analytics.
            </p>
            <div className="space-y-2 text-sm text-blue-700">
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4" />
                <span>Ask about trends, comparisons, and forecasts</span>
              </div>
              <div className="flex items-center space-x-2">
                <PieChart className="w-4 h-4" />
                <span>Request charts, tables, and visualizations</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Filter by date ranges and specific criteria</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => onSuggestionClick('What data do I have available?')}
          variant="outline"
          size="sm"
        >
          <Target className="w-4 h-4 mr-2" />
          Explore Data
        </Button>
        <Button
          onClick={() => onSuggestionClick('Show me key metrics')}
          variant="outline"
          size="sm"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Key Metrics
        </Button>
        <Button
          onClick={() => onSuggestionClick('Create a dashboard')}
          variant="outline"
          size="sm"
        >
          <PieChart className="w-4 h-4 mr-2" />
          Dashboard
        </Button>
      </div>
    </div>
  );
};

export default AnalyticsChatSuggestions;